import { useState, useEffect } from 'react';
import { Table, Button, Badge, Progress, Space, Modal, Form, Input, InputNumber, Switch, message, Tooltip } from 'antd';
import { PlusOutlined, EditOutlined, EyeOutlined, TeamOutlined, KeyOutlined } from '@ant-design/icons';
import axios from 'axios';

const IAM_API_URL = 'http://localhost:8000/api/v1';

interface Organization {
  id: string;
  name: string;
  description?: string;
  domain?: string;
  maxUsers: number;
  maxDocuments: number;
  maxStorage: number;
  isActive: boolean;
  allowedDomains: string[];
  documentRetentionDays: number;
  allowPublicDocuments: boolean;
  maxQueryRate: number;
  embeddingModel: string;
  createdAt: string;
  updatedAt: string;
  _count?: {
    users: number;
    apiKeys: number;
  };
}

export function OrganizationsPage() {
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [detailsModalVisible, setDetailsModalVisible] = useState(false);
  const [selectedOrg, setSelectedOrg] = useState<Organization | null>(null);
  const [form] = Form.useForm();

  useEffect(() => {
    fetchOrganizations();
  }, []);

  const fetchOrganizations = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${IAM_API_URL}/organizations`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setOrganizations(response.data);
    } catch (error: any) {
      console.error('Error fetching organizations:', error);
      message.error(error.response?.data?.message || 'Failed to load organizations');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setSelectedOrg(null);
    form.resetFields();
    form.setFieldsValue({
      maxUsers: 50,
      maxDocuments: 1000,
      maxStorage: 5368709120,
      isActive: true,
      documentRetentionDays: 365,
      allowPublicDocuments: false,
      maxQueryRate: 100,
      embeddingModel: 'text-embedding-ada-002',
      allowedDomains: [],
    });
    setModalVisible(true);
  };

  const handleEdit = (org: Organization) => {
    setSelectedOrg(org);
    form.setFieldsValue({
      ...org,
      allowedDomains: org.allowedDomains.join(', '),
    });
    setModalVisible(true);
  };

  const handleViewDetails = (org: Organization) => {
    setSelectedOrg(org);
    setDetailsModalVisible(true);
  };

  const handleSubmit = async (values: any) => {
    try {
      const token = localStorage.getItem('token');
      const payload = {
        ...values,
        allowedDomains: typeof values.allowedDomains === 'string'
          ? values.allowedDomains.split(',').map((d: string) => d.trim()).filter(Boolean)
          : values.allowedDomains || [],
      };

      if (selectedOrg) {
        await axios.patch(`${IAM_API_URL}/organizations/${selectedOrg.id}`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
        message.success('Organization updated successfully');
      } else {
        await axios.post(`${IAM_API_URL}/organizations`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
        message.success('Organization created successfully');
      }

      setModalVisible(false);
      fetchOrganizations();
    } catch (error: any) {
      console.error('Error saving organization:', error);
      message.error(error.response?.data?.message || 'Failed to save organization');
    }
  };

  const formatBytes = (bytes: number) => {
    const gb = bytes / (1024 * 1024 * 1024);
    return `${gb.toFixed(2)} GB`;
  };

  const columns = [
    {
      title: 'Organization',
      dataIndex: 'name',
      key: 'name',
      render: (name: string, record: Organization) => (
        <div>
          <div className="font-semibold text-gray-900">{name}</div>
          {record.description && (
            <div className="text-sm text-gray-500">{record.description}</div>
          )}
          {record.domain && (
            <div className="text-xs text-gray-400">{record.domain}</div>
          )}
        </div>
      ),
    },
    {
      title: 'Users',
      key: 'users',
      width: 200,
      render: (_: any, record: Organization) => {
        const userCount = record._count?.users || 0;
        const percentage = (userCount / record.maxUsers) * 100;
        return (
          <div>
            <div className="flex items-center gap-2 mb-1">
              <TeamOutlined />
              <span className="text-sm">
                {userCount} / {record.maxUsers}
              </span>
            </div>
            <Progress
              percent={percentage}
              size="small"
              status={percentage > 90 ? 'exception' : percentage > 70 ? 'normal' : 'active'}
              showInfo={false}
            />
          </div>
        );
      },
    },
    {
      title: 'API Keys',
      key: 'apiKeys',
      width: 120,
      render: (_: any, record: Organization) => (
        <div className="flex items-center gap-2">
          <KeyOutlined />
          <span className="text-sm">{record._count?.apiKeys || 0}</span>
        </div>
      ),
    },
    {
      title: 'Storage Limit',
      dataIndex: 'maxStorage',
      key: 'maxStorage',
      width: 120,
      render: (bytes: number) => (
        <span className="text-sm">{formatBytes(bytes)}</span>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'isActive',
      key: 'isActive',
      width: 100,
      render: (isActive: boolean) => (
        <Badge
          status={isActive ? 'success' : 'default'}
          text={isActive ? 'Active' : 'Inactive'}
        />
      ),
    },
    {
      title: 'Created',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 120,
      render: (date: string) => new Date(date).toLocaleDateString(),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 150,
      render: (_: any, record: Organization) => (
        <Space size="small">
          <Tooltip title="View Details">
            <Button
              type="text"
              icon={<EyeOutlined />}
              onClick={() => handleViewDetails(record)}
            />
          </Tooltip>
          <Tooltip title="Edit">
            <Button
              type="text"
              icon={<EditOutlined />}
              onClick={() => handleEdit(record)}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Organizations</h1>
              <p className="text-gray-600 mt-1">
                Manage organizations, users, and resource limits
              </p>
            </div>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleCreate}
              size="large"
            >
              Create Organization
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-lg shadow p-4">
            <div className="text-sm text-gray-500">Total Organizations</div>
            <div className="text-2xl font-bold text-gray-900">{organizations.length}</div>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <div className="text-sm text-gray-500">Active Organizations</div>
            <div className="text-2xl font-bold text-green-600">
              {organizations.filter((o) => o.isActive).length}
            </div>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <div className="text-sm text-gray-500">Total Users</div>
            <div className="text-2xl font-bold text-blue-600">
              {organizations.reduce((sum, o) => sum + (o._count?.users || 0), 0)}
            </div>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <div className="text-sm text-gray-500">Total API Keys</div>
            <div className="text-2xl font-bold text-purple-600">
              {organizations.reduce((sum, o) => sum + (o._count?.apiKeys || 0), 0)}
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="bg-white rounded-lg shadow">
          <Table
            columns={columns}
            dataSource={organizations}
            loading={loading}
            rowKey="id"
            pagination={{
              pageSize: 10,
              showSizeChanger: true,
              showTotal: (total) => `Total ${total} organizations`,
            }}
          />
        </div>
      </div>

      {/* Create/Edit Modal */}
      <Modal
        title={selectedOrg ? 'Edit Organization' : 'Create Organization'}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        onOk={() => form.submit()}
        width={700}
        okText={selectedOrg ? 'Update' : 'Create'}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
        >
          <Form.Item
            label="Organization Name"
            name="name"
            rules={[{ required: true, message: 'Please enter organization name' }]}
          >
            <Input placeholder="e.g., Thiran Technologies" />
          </Form.Item>

          <Form.Item label="Description" name="description">
            <Input.TextArea rows={2} placeholder="Brief description of the organization" />
          </Form.Item>

          <div className="grid grid-cols-2 gap-4">
            <Form.Item label="Domain" name="domain">
              <Input placeholder="e.g., thiran.ai" />
            </Form.Item>

            <Form.Item
              label="Allowed Email Domains"
              name="allowedDomains"
              tooltip="Comma-separated list of allowed email domains"
            >
              <Input placeholder="e.g., thiran.ai, thirantech.com" />
            </Form.Item>
          </div>

          <div className="grid grid-cols-3 gap-4">
            <Form.Item label="Max Users" name="maxUsers" rules={[{ required: true }]}>
              <InputNumber min={1} style={{ width: '100%' }} />
            </Form.Item>

            <Form.Item label="Max Documents" name="maxDocuments" rules={[{ required: true }]}>
              <InputNumber min={1} style={{ width: '100%' }} />
            </Form.Item>

            <Form.Item label="Max Storage (bytes)" name="maxStorage" rules={[{ required: true }]}>
              <InputNumber min={1} style={{ width: '100%' }} />
            </Form.Item>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <Form.Item label="Document Retention (days)" name="documentRetentionDays">
              <InputNumber min={1} style={{ width: '100%' }} />
            </Form.Item>

            <Form.Item label="Max Query Rate" name="maxQueryRate">
              <InputNumber min={1} style={{ width: '100%' }} />
            </Form.Item>
          </div>

          <Form.Item label="Embedding Model" name="embeddingModel">
            <Input placeholder="e.g., text-embedding-ada-002" />
          </Form.Item>

          <div className="grid grid-cols-2 gap-4">
            <Form.Item label="Active Status" name="isActive" valuePropName="checked">
              <Switch />
            </Form.Item>

            <Form.Item label="Allow Public Documents" name="allowPublicDocuments" valuePropName="checked">
              <Switch />
            </Form.Item>
          </div>
        </Form>
      </Modal>

      {/* Details Modal */}
      <Modal
        title="Organization Details"
        open={detailsModalVisible}
        onCancel={() => setDetailsModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setDetailsModalVisible(false)}>
            Close
          </Button>,
          <Button
            key="edit"
            type="primary"
            icon={<EditOutlined />}
            onClick={() => {
              setDetailsModalVisible(false);
              if (selectedOrg) handleEdit(selectedOrg);
            }}
          >
            Edit
          </Button>,
        ]}
        width={700}
      >
        {selectedOrg && (
          <div className="space-y-4">
            <div>
              <h3 className="text-lg font-semibold mb-2">Basic Information</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <span className="text-gray-500">Name:</span>
                  <div className="font-medium">{selectedOrg.name}</div>
                </div>
                <div>
                  <span className="text-gray-500">Domain:</span>
                  <div className="font-medium">{selectedOrg.domain || '-'}</div>
                </div>
                <div className="col-span-2">
                  <span className="text-gray-500">Description:</span>
                  <div className="font-medium">{selectedOrg.description || '-'}</div>
                </div>
              </div>
            </div>

            <div>
              <h3 className="text-lg font-semibold mb-2">Resource Limits</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <span className="text-gray-500">Max Users:</span>
                  <div className="font-medium">{selectedOrg.maxUsers}</div>
                </div>
                <div>
                  <span className="text-gray-500">Max Documents:</span>
                  <div className="font-medium">{selectedOrg.maxDocuments}</div>
                </div>
                <div>
                  <span className="text-gray-500">Max Storage:</span>
                  <div className="font-medium">{formatBytes(selectedOrg.maxStorage)}</div>
                </div>
                <div>
                  <span className="text-gray-500">Max Query Rate:</span>
                  <div className="font-medium">{selectedOrg.maxQueryRate} req/min</div>
                </div>
              </div>
            </div>

            <div>
              <h3 className="text-lg font-semibold mb-2">Settings</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <span className="text-gray-500">Document Retention:</span>
                  <div className="font-medium">{selectedOrg.documentRetentionDays} days</div>
                </div>
                <div>
                  <span className="text-gray-500">Embedding Model:</span>
                  <div className="font-medium text-sm">{selectedOrg.embeddingModel}</div>
                </div>
                <div>
                  <span className="text-gray-500">Allow Public Documents:</span>
                  <div className="font-medium">
                    <Badge
                      status={selectedOrg.allowPublicDocuments ? 'success' : 'default'}
                      text={selectedOrg.allowPublicDocuments ? 'Yes' : 'No'}
                    />
                  </div>
                </div>
                <div>
                  <span className="text-gray-500">Status:</span>
                  <div className="font-medium">
                    <Badge
                      status={selectedOrg.isActive ? 'success' : 'default'}
                      text={selectedOrg.isActive ? 'Active' : 'Inactive'}
                    />
                  </div>
                </div>
              </div>
            </div>

            {selectedOrg.allowedDomains.length > 0 && (
              <div>
                <h3 className="text-lg font-semibold mb-2">Allowed Email Domains</h3>
                <div className="flex flex-wrap gap-2">
                  {selectedOrg.allowedDomains.map((domain) => (
                    <Badge key={domain} count={domain} style={{ backgroundColor: '#1890ff' }} />
                  ))}
                </div>
              </div>
            )}

            <div>
              <h3 className="text-lg font-semibold mb-2">Statistics</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <span className="text-gray-500">Total Users:</span>
                  <div className="font-medium">{selectedOrg._count?.users || 0}</div>
                </div>
                <div>
                  <span className="text-gray-500">Total API Keys:</span>
                  <div className="font-medium">{selectedOrg._count?.apiKeys || 0}</div>
                </div>
                <div>
                  <span className="text-gray-500">Created:</span>
                  <div className="font-medium">{new Date(selectedOrg.createdAt).toLocaleString()}</div>
                </div>
                <div>
                  <span className="text-gray-500">Last Updated:</span>
                  <div className="font-medium">{new Date(selectedOrg.updatedAt).toLocaleString()}</div>
                </div>
              </div>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}

export default OrganizationsPage;
